<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useDebounce, useEventListener } from '@vueuse/core';
import { CURRENCY } from '@Constants';
import type { FieldSchema } from '../../../types';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    currency?: CURRENCY;
    error?: string | boolean;
    field?: FieldSchema;
    placeholder?: string;
    required?: boolean;
    resetZeroModelOnFocus?: boolean;
  }>(),
  {
    currency: CURRENCY.USD,
    placeholder: 'components.forms.placeholder.common',
  }
);

let stopEventListener: (() => void) | undefined;

const model = defineModel<number | string | null>({ required: true, default: null });

const _model = ref<number | null>(props.field?.model ? Number(props.field?.model) : null);
const inputRef = ref<any>(null);

const inputElement = computed(() => inputRef.value?.$el?.querySelector('input'));

const debouncedModel = useDebounce(_model, 200);

watch(debouncedModel, (value) => (model.value = value));
watch(model, () => initInternalModel());

onMounted(() => {
  initInternalModel();
  nextTick(() => handleCutOnStart());
});

onUnmounted(() => stopEventListener?.());

function handleCutOnStart() {
  const element = inputElement.value;
  if (!element) return;

  stopEventListener = useEventListener(element, 'cut', (event: ClipboardEvent) => {
    event.preventDefault();

    const isRequiredAndAllowedZero = props.field?.settings.required && props.field?.settings.allowZeroAsValidModel;
    _model.value = isRequiredAndAllowedZero ? 0 : null;
  });
}

function initInternalModel() {
  // NOTE: To accept 0 as a valid value add the allowZeroAsValidModel property to the field settings in the form.
  const isRequiredAndAllowedZero = props.field?.settings.required && props.field?.settings.allowZeroAsValidModel;
  const isInvalid = model.value === null || model.value === undefined || Number.isNaN(Number(model.value));
  const requiredAndZeroValidation = isRequiredAndAllowedZero ? 0 : null;

  _model.value = isInvalid ? requiredAndZeroValidation : Number(model.value);
}

function handleFocus() {
  const checkZeroModel = props.resetZeroModelOnFocus && _model.value === 0;
  if (!checkZeroModel) return;

  _model.value = null;
}
</script>

<template>
  <InputNumber
    ref="inputRef"
    v-model="_model"
    mode="currency"
    :class="['h-9 w-full', { 'af-field-error': !!error }]"
    :currency="field?.settings.currency || currency"
    :invalid="!!error"
    :placeholder="$t(field?.settings.placeholder || placeholder)"
    :pt:input:root="['text-sm', { 'af-field-error': !!error }]"
    :required="field?.settings.required || required"
    @focus="handleFocus"
    @input="_model = Number($event.value)"
  />
</template>
